import { getPhonebookProfilePath } from "@/common/helpers/get-phonebook-profile-path";
import { BadgeColor, CustomBadgeColor, IconBadge } from "@/components/icon-badge/icon-badge";
import { UserItem } from "@/components/user-item/user-item";
import { HammerIcon, ScaleIcon, SquareStack, TimerIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ExtendedUser } from "../_hooks/use-group-users";

interface GroupUserCardProps {
  user: ExtendedUser;
}

const TEMPORARY_COLOR: CustomBadgeColor = {
  background: "bg-cyan-500/10",
  foreground: "text-cyan-600",
  tooltipClassName: "border border-cyan-500/50 bg-background text-cyan-600",
};

const RULE_COLOR: CustomBadgeColor = {
  background: "bg-blue-500/10",
  foreground: "text-blue-600",
  tooltipClassName: "border border-blue-500/50 bg-background text-blue-600",
};

export function GroupUserCard({ user }: GroupUserCardProps) {
  const { t } = useTranslation(["ram"]);

  const duplicateSources = [
    user.rule ? t("ram:DuplicateSourceRule") : null,
    user.temporary ? t("ram:DuplicateSourceTemporary") : null,
    user.manual ? t("ram:DuplicateSourceManual") : null,
  ].filter((source): source is string => Boolean(source));

  const duplicateTooltip = t("ram:DuplicateUserTooltip", {
    sources: duplicateSources.join(", "),
  });

  return (
    <UserItem
      user={user}
      to={`~${getPhonebookProfilePath(user.uuid)}`}
      showEmail
      endContent={
        user.duplicate ? (
          <IconBadge icon={SquareStack} color={BadgeColor.Warning} tooltip={duplicateTooltip} />
        ) : (
          <>
            {user.rule && <IconBadge icon={ScaleIcon} color={RULE_COLOR} tooltip={t("ram:RuleBasedUser")} />}
            {user.temporary && <IconBadge icon={TimerIcon} color={TEMPORARY_COLOR} tooltip={t("ram:TemporaryUser")} />}
            {user.manual && (
              <IconBadge icon={HammerIcon} color={BadgeColor.Success} tooltip={t("ram:ManuallyAddedUser")} />
            )}
          </>
        )
      }
    />
  );
}
